<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('apia.fertilization_plan')"
      :grid-ref="$refs[gridRefKey]"
      :show-add="false"
      :show-filter="false"
      :show-columns="false"
    />
    <div class="row dash_filter pt-1 pb-2" style="display: flex; align-items: center">
      <div class="form-group text-left season_year_select col-3" style="margin-right: 20px">
        <DxSelectBox
          v-model="local_season_uuid"
          :data-source="seasons"
          display-expr="name"
          value-expr="uuid"
          :on-value-changed="onSeasonChanged"
          placeholder=""
        />
      </div>
      <div class="form-group text-right department_select col-6">
        <DxTagBox
          v-model="selected_departments"
          :data-source="departments"
          display-expr="name"
          value-expr="uuid"
          :show-clear-button="true"
          :search-enabled="true"
          :placeholder="$t('common.select_department')"
          @value-changed="onDepartmentsChanged"
        />
      </div>
    </div>

    <DxDataGrid
      :data-source="report_dataset"
      :remote-operations="gridRemoteOperations"
      :showBorders="true"
      :show-column-lines="true"
      :show-row-lines="false"
      :word-wrap-enabled="true"
      :row-alternation-enabled="true"
      :ref="gridRefKey"
      :element-attr="gridAttributes"
      @exporting="onExporting"
      key-expr="UUID"
    >
      <DxColumn caption="Planificat" alignment="center">
        <DxColumn
          data-field="field_number"
          caption="Bloc fizic nr."
          css-class="responsive-column"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
        <DxColumn caption="Parcela" alignment="center">
          <DxColumn
            data-field="field_name"
            caption="Nr."
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
            alignment="center"
          />
          <DxColumn
            data-field="area_ha"
            caption="Suprafata (ha)"
            :allowHeaderFiltering="false"
            :filter-operations="['contains']"
            :allowSorting="false"
            alignment="center"
          />
        </DxColumn>
        <DxColumn
          data-field="culture_name"
          caption="Cultura"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
        <DxColumn
          data-field="planned_quantity"
          caption="Planificat N Kg s.a. /ha"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
      </DxColumn>

      <DxColumn caption="Realizat" alignment="center">
        <DxColumn caption="Ingrasaminte aplicate" alignment="center">
          <DxColumn caption="Organice" alignment="center">
            <DxColumn
              data-field="organic_product_name"
              caption="Tip"
              css-class="responsive-column"
              :allowHeaderFiltering="false"
              :filter-operations="['contains']"
              :allowSorting="false"
              alignment="center"
            />
            <DxColumn
              data-field="organic_t_ha"
              caption="t/ha"
              :allowHeaderFiltering="false"
              :filter-operations="['contains']"
              :allowSorting="false"
              alignment="center"
            />
            <DxColumn
              data-field="organic_kg_n_ha"
              caption="kg N/ha"
              :allowHeaderFiltering="false"
              :filter-operations="['contains']"
              :allowSorting="false"
              alignment="center"
            />
          </DxColumn>
          <DxColumn caption="Minerale" alignment="center">
            <DxColumn
              data-field="mineral_product_name"
              caption="Tip"
              css-class="responsive-column"
              :allowHeaderFiltering="false"
              :filter-operations="['contains']"
              :allowSorting="false"
              alignment="center"
            />
            <DxColumn
              data-field="mineral_t_ha"
              caption="t/ha"
              :allowHeaderFiltering="false"
              :filter-operations="['contains']"
              :allowSorting="false"
              alignment="center"
            />
            <DxColumn
              data-field="mineral_kg_n_ha"
              caption="kg N/ha"
              :allowHeaderFiltering="false"
              :filter-operations="['contains']"
              :allowSorting="false"
              alignment="center"
            />
          </DxColumn>
        </DxColumn>
        <DxColumn
          data-field="date_of_treatment"
          caption="Data aplic."
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
        <DxColumn
          data-field="sum_kg_n_ha"
          caption="Total N Kg s.a. /ha"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
      </DxColumn>

      <DxColumn
        dataField="notes"
        caption="Obs."
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
      ></DxColumn>

      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'RegFer'" />
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxHeaderFilter :visible="false" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxGrouping :auto-expand-all="true" />
      <DxScrolling column-rendering-mode="virtual" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxButton,
  DxPopup,
  DxToolbar,
  DxItem,
  DxExport,
  DxGroupPanel,
  DxGrouping,
  DxScrolling,
} from "devextreme-vue/data-grid";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxTagBox } from "devextreme-vue/tag-box";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "FertilizationPlan",
  components: {
    DxDataGrid,
    DxColumn,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxButton,
    DxPaging,
    DxPager,
    DxPopup,
    DxToolbar,
    DxItem,
    Forbidden,
    DxExport,
    DxGroupPanel,
    DxGrouping,
    DxScrolling,
    DxSelectBox,
    DxTagBox,
    GridPanelHeader,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid fertilization-grid",
      },
      gridRefKey: "fertilizationGrid",
      report_dataset: [],
      gridRemoteOperations: {
        paging: false,
        filtering: false,
        sorting: false,
      },
      pageSizes: [15, 25, 100],
      local_season_uuid: null,
      selected_departments: [],
    };
  },
  watch: {
    seasons: {
      handler: function (newVal) {
        if (newVal.length > 0) {
          this.local_season_uuid = newVal[0].uuid;
          this.load();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
      seasons: "seasons/seasons",
      departments: "company/departments",
    }),
    config() {
      return {
        permission: permissions.APIA,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getReportsData: "reporting/getReportsData",
      getSeasons: "seasons/getSeasons",
      getDepartments: "company/getDepartments",
    }),
    async load() {
      if (!this.local_season_uuid) {
        this.report_dataset = [];
        return;
      }

      let payload = {
        limit: 999999,
        name: "Fertilization_Plan",
        args: {
          season_uuid: this.local_season_uuid,
          department_uuids: this.selected_departments.join(","),
        },
      };

      try {
        this.getReportsData(payload).then((response) => {
          this.report_dataset = response;
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
      }
    },
    onExporting(e) {
      e.fileName = "RegFer_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        //const fields = ["deleted_at", "finished_weighing", "has_stock_movement"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("RegFer");

        // Visible column count (so we can merge header cells across all columns)
        const visibleCols = e.component.getVisibleColumns().filter((c) => c.type !== "group" && c.dataField !== undefined);
        const colCount = visibleCols.length;

        // Helper to merge a full-width row and set value
        const put = (rowIndex, text, opts = {}) => {
          const startCell = worksheet.getCell(rowIndex, 1);
          const endCell = worksheet.getCell(rowIndex, colCount);
          worksheet.mergeCells(rowIndex, 1, rowIndex, colCount);
          startCell.value = text;
          startCell.alignment = { wrapText: true, horizontal: opts.center ? "center" : "left", vertical: "top" };
          if (opts.bold) startCell.font = { bold: true };
          if (opts.italic) startCell.font = { italic: true };
          if (opts.size) startCell.font = { ...(startCell.font || {}), size: opts.size };
        };

        // Header lines (you can adjust dotted fillers)
        const headerLines = ["Plan de fertilizare (model simplificat)"];

        // Write header
        let currentRow = 1;
        put(currentRow++, headerLines[0], { bold: true, center: true, size: 14 });

        // Leave one blank row before grid
        currentRow++;

        exportDataGrid({
          component: e.component,
          worksheet,
          topLeftCell: { row: currentRow, column: 1 }, // start grid after header
          autoFilterEnabled: true,
        })
          .then((cellRange) => {
            // Optional: freeze header + grid header rows
            worksheet.views = [{ state: "frozen", ySplit: currentRow - 1 }];

            return workbook.xlsx.writeBuffer();
          })
          .then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
      }
    },
    onSeasonChanged(e) {
      this.local_season_uuid = e.value;
      this.load();
    },
    onDepartmentsChanged(e) {
      this.selected_departments = e.value;
      this.load();
    },
  },
  mounted() {
    const params = {
      limit: 1000,
      sort: [
        {
          selector: "year",
          desc: true,
        },
      ],
    };
    this.getSeasons(params);
    this.getDepartments({ limit: 1000 });

    this.load();
  },
};
</script>

<style scoped lang="scss">
.fertilization-grid {
  height: calc(95vh - 200px);
}
</style>
